"use client";

import React, {JSX} from "react";

import {BookingSummaryCollapsible, CollapsibleRow, SummaryRow, translateFeeLabel} from "@/entities/booking";
import {RentSummary} from "@/entities/booking";

import {useTranslation} from "@/shared/i18n";

import "@/features/rental/check-availability/styles/pricing-skeleton.scss";

interface Props {
    loading?: boolean;
    rentSummary?: RentSummary | null;
    feesAmounts?: Array<CollapsibleRow>;
    taxesAmounts?: Array<CollapsibleRow>;
    securityDeposits?: Array<CollapsibleRow>;
    couponDiscount?: string | null;
    totalPrice?: string | null;
    totalWithDeposits?: string | null;
}

export function PricingSkeleton({
    loading,
    rentSummary,
    feesAmounts = [],
    taxesAmounts = [],
    securityDeposits = [],
    couponDiscount,
    totalPrice,
    totalWithDeposits,
}: Props): JSX.Element {
    const {t, dict} = useTranslation("booking");
    const {t: tRental} = useTranslation("rental");

    if (loading) {
        return (
            <div className="pricing-skeleton">
                <div className="pricing-skeleton__row">
                    <span className="pricing-skeleton__label" />
                    <span className="pricing-skeleton__amount" />
                </div>
                <div className="pricing-skeleton__row">
                    <span className="pricing-skeleton__label pricing-skeleton__label--short" />
                    <span className="pricing-skeleton__amount" />
                </div>
                <div className="pricing-skeleton__row pricing-skeleton__row--total">
                    <span className="pricing-skeleton__label" />
                    <span className="pricing-skeleton__amount pricing-skeleton__amount--large" />
                </div>
            </div>
        );
    }

    const hasContent = rentSummary || feesAmounts.length > 0 || taxesAmounts.length > 0 || securityDeposits.length > 0 || totalPrice;

    if (!hasContent) {
        return <></>;
    }

    // Translate rent summary label
    const rentSummaryLabel = rentSummary ? tRental(rentSummary.labelKey as Parameters<typeof tRental>[0], rentSummary.labelParams) : null;

    // Translate fee labels - if translation not found, use original label from API
    const translatedFees = feesAmounts.map((fee) => ({
        ...fee,
        label: translateFeeLabel(fee.label, t, dict),
    }));

    // Translate tax labels
    const translatedTaxes = taxesAmounts.map((tax) => ({
        ...tax,
        label: translateFeeLabel(tax.label, t, dict),
    }));

    return (
        <>
            {rentSummary && rentSummaryLabel && <SummaryRow label={rentSummaryLabel} amount={rentSummary.amount} />}
            {translatedFees.length > 0 && <BookingSummaryCollapsible title={t("fees")} rows={translatedFees} />}
            {translatedTaxes.length > 0 && <BookingSummaryCollapsible title={t("taxes")} rows={translatedTaxes} />}
            {couponDiscount && <SummaryRow label={t("promoDiscount")} amount={couponDiscount} discount />}
            {totalPrice && <SummaryRow label={t("totalExclTaxes")} amount={totalPrice} highlighted />}
            {securityDeposits.length > 0 && <BookingSummaryCollapsible title={t("securityDeposit")} rows={securityDeposits} />}
            {totalWithDeposits && <SummaryRow label={t("totalWithDeposits")} amount={totalWithDeposits} highlighted />}
        </>
    );
}
